/**
 * FS Cart Drawer — Déesse PRO
 * Vanilla JS, no jQuery. Shopify AJAX API (cart.js / cart/add.js / cart/change.js / cart/update.js).
 * Ported from pwt-sitespeed-c/native-cart-drawer.js with upsell, recs, cart-note, Redo, multi-tier rewards removed.
 */
(function () {
  'use strict';

  var SELECTORS = {
    drawer: '#FsCartDrawer',
    overlay: '.fs-cart-overlay',
    panel: '.fs-cart-panel',
    closeBtn: '[data-action="close-drawer"]',
    cartItems: '[data-cart-items]',
    cartCount: '[data-cart-count]',
    cartSubtotal: '[data-cart-subtotal]',
    cartDiscounts: '[data-cart-discounts]',
    shippingBar: '[data-shipping-bar]',
    shippingFill: '[data-shipping-fill]',
    shippingMessage: '[data-shipping-message]',
    cartIconLinks: '.js-fs-cart-open',
    cartBubble: '#cart-icon-bubble'
  };

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ROUTES = {
    cart: ROOT + 'cart.js',
    cartAdd: ROOT + 'cart/add.js',
    cartChange: ROOT + 'cart/change.js',
    cartUpdate: ROOT + 'cart/update.js',
    cartClear: ROOT + 'cart/clear.js'
  };

  var drawer;
  var isOpen = false;
  var SHIPPING_ENABLED = false;
  var SHIPPING_THRESHOLD = 0;
  var SHIPPING_PROGRESS_MSG = '';
  var SHIPPING_QUALIFIED_MSG = '';
  var CURRENCY = 'GBP';

  // ─── Helpers ───────────────────────────────────────
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function escapeHtml(str) {
    if (str == null) return '';
    var div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }

  function formatMoney(cents) {
    var format = window.theme && window.theme.moneyFormat;
    if (format && typeof format === 'string') {
      var amount = (cents / 100).toFixed(2);
      var amountNoDecimals = Math.floor(cents / 100);
      var amountWithComma = amount.replace('.', ',');
      return format
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount}}', amount);
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: CURRENCY
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function getSizedImageUrl(src, size) {
    if (!src) return '';
    var match = src.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
    if (match) {
      var ext = match[0];
      return src.replace(ext, '_' + size + ext);
    }
    return src;
  }

  function fetchJSON(url, opts) {
    opts = opts || {};
    opts.headers = opts.headers || {};
    opts.headers['Accept'] = 'application/json';
    if (opts.method === 'POST') {
      opts.headers['Content-Type'] = 'application/json';
    }
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, 10000);
    opts.signal = controller.signal;
    return fetch(url, opts).then(function (r) {
      clearTimeout(timeoutId);
      return r.text().then(function (text) {
        var data;
        try { data = JSON.parse(text); } catch (e) {
          return Promise.reject({ description: 'Unexpected server response.', status: r.status });
        }
        if (!r.ok) {
          if (!data.description) data.description = data.message || 'Something went wrong (' + r.status + ')';
          return Promise.reject(data);
        }
        return data;
      });
    }).catch(function (err) {
      clearTimeout(timeoutId);
      if (err && err.name === 'AbortError') return Promise.reject({ description: 'Request timed out. Please try again.' });
      if (!err || !err.description) return Promise.reject({ description: (err && err.message) || 'Something went wrong.' });
      return Promise.reject(err);
    });
  }

  // ─── Cart operation queue ─────────────────────────
  var _cartQueue = Promise.resolve();
  function enqueueCartOp(fn) {
    _cartQueue = _cartQueue.then(fn, fn);
    return _cartQueue;
  }

  // Notify subscribers (e.g. gwp-bundle.js) that the cart changed via a
  // user-initiated drawer op. Only emit for user actions — never from
  // refreshDrawer() or in response to our own subscribers, to avoid loops.
  function notifyCartUpdate(cart, source) {
    if (typeof window.publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    try {
      window.publish(PUB_SUB_EVENTS.cartUpdate, {
        source: source || 'fs-cart-drawer',
        cartData: cart || null
      });
    } catch (err) {
      // swallow — subscriber errors must never break the drawer
    }
  }

  // ─── Drawer open/close ─────────────────────────────
  function openDrawer() {
    if (isOpen || !drawer) return;
    isOpen = true;
    drawer.classList.add('is-open');
    document.body.classList.add('fs-cart-open');
    var closeBtn = qs('.fs-cart-close', drawer);
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 100);
  }

  function closeDrawer() {
    if (!isOpen || !drawer) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    document.body.classList.remove('fs-cart-open');
  }

  // ─── Cart API ──────────────────────────────────────
  function fetchCart() {
    return fetchJSON(ROUTES.cart);
  }

  // If the only items left in the cart are gift-with-purchase (GWP) items
  // (flagged via properties._gwp === 'true'), clear them. GWP items are
  // tied to paid products — keeping them orphaned would let a customer
  // check out with only the free gift.
  function pruneOrphanedGwp(cart) {
    if (!cart || !cart.items || !cart.items.length) return Promise.resolve(cart);
    var hasPaid = false;
    var hasGwp = false;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.properties && it.properties._gwp === 'true') hasGwp = true;
      else hasPaid = true;
    }
    if (hasGwp && !hasPaid) {
      return fetchJSON(ROUTES.cartClear, { method: 'POST', body: JSON.stringify({}) }).catch(function (err) {
        console.error('FsCart GWP prune error:', err);
        return cart;
      });
    }
    return Promise.resolve(cart);
  }

  function addToCart(variantId, qty, properties) {
    return enqueueCartOp(function () {
      var item = { id: variantId, quantity: qty || 1 };
      if (properties) item.properties = properties;
      drawer.classList.add('is-loading');
      return fetchJSON(ROUTES.cartAdd, {
        method: 'POST',
        body: JSON.stringify({ items: [item] })
      }).then(function () {
        return refreshDrawer();
      }).then(function () {
        openDrawer();
        drawer.classList.remove('is-loading');
        notifyCartUpdate(null, 'fs-cart-drawer:add');
      }).catch(function (err) {
        console.error('FsCart add error:', err);
        drawer.classList.remove('is-loading');
        return refreshDrawer().then(function () {
          openDrawer();
          showCartError(err);
        });
      });
    });
  }

  function applyCartToDom(cart) {
    updateCartCount(cart.item_count);
    updateSubtotal(cart.total_price);
    updateShippingBar(cart.total_price);
    updateDiscounts(cart);
    updateItems(cart);
    updateRecommendation(cart);
    updateEmptyState(cart.item_count);
    updateHeaderBubble(cart.item_count);
  }

  // Toggle the cross-sell card based on cart contents.
  // Show when the configured trigger product is in cart AND the recommended
  // product is NOT. Configuration comes from data attributes set by Liquid.
  function updateRecommendation(cart) {
    var rec = qs('[data-recommendation]', drawer);
    if (!rec) return;
    var triggerId = parseInt(rec.getAttribute('data-trigger-product-id'), 10);
    var recommendedId = parseInt(rec.getAttribute('data-recommended-product-id'), 10);
    if (!triggerId || !recommendedId) { rec.style.display = 'none'; return; }
    var hasTrigger = false;
    var hasRecommended = false;
    for (var i = 0; i < cart.items.length; i++) {
      var pid = cart.items[i].product_id;
      if (pid === triggerId) hasTrigger = true;
      if (pid === recommendedId) hasRecommended = true;
    }
    rec.style.display = (hasTrigger && !hasRecommended) ? '' : 'none';
  }

  function updateQuantity(key, qty) {
    return enqueueCartOp(function () {
      drawer.classList.add('is-loading');
      return fetchJSON(ROUTES.cartChange, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (cart) {
        return pruneOrphanedGwp(cart);
      }).then(function (cart) {
        applyCartToDom(cart);
        drawer.classList.remove('is-loading');
        notifyCartUpdate(cart, qty === 0 ? 'fs-cart-drawer:remove' : 'fs-cart-drawer:change');
      }).catch(function (err) {
        console.error('FsCart change error:', err);
        showCartError(err);
        drawer.classList.remove('is-loading');
      });
    });
  }

  function removeItem(key, el) {
    if (el) {
      var item = el.closest('.fs-cart-item');
      if (item) {
        item.classList.add('is-removing');
        setTimeout(function () { updateQuantity(key, 0); }, 300);
        return;
      }
    }
    updateQuantity(key, 0);
  }

  function showCartError(err) {
    var msg = (err && err.description) || (err && err.message) || 'Something went wrong. Please try again.';
    var body = qs('.fs-cart-body', drawer);
    if (!body) return;
    var existing = qs('.fs-cart-error', drawer);
    if (existing) existing.remove();
    var el = document.createElement('div');
    el.className = 'fs-cart-error';
    el.textContent = msg;
    body.insertBefore(el, body.firstChild);
    setTimeout(function () { if (el.parentNode) el.remove(); }, 4000);
  }

  // ─── Refresh drawer ────────────────────────────────
  var _refreshPending = null;
  function refreshDrawer() {
    if (_refreshPending) return _refreshPending;
    _refreshPending = fetchCart()
      .then(pruneOrphanedGwp)
      .then(function (cart) {
        if (cart.currency) CURRENCY = cart.currency;
        applyCartToDom(cart);
      }).finally(function () {
        _refreshPending = null;
      });
    return _refreshPending;
  }

  function updateCartCount(count) {
    qsa(SELECTORS.cartCount, drawer).forEach(function (el) {
      el.textContent = '(' + count + ')';
    });
  }

  function updateSubtotal(totalPrice) {
    var el = qs(SELECTORS.cartSubtotal, drawer);
    if (el) el.textContent = formatMoney(totalPrice);
  }

  function updateShippingBar(totalPrice) {
    if (!SHIPPING_ENABLED) return;
    var bar = qs(SELECTORS.shippingBar, drawer);
    if (!bar) return;
    var fill = qs(SELECTORS.shippingFill, bar);
    var messageEl = qs(SELECTORS.shippingMessage, bar);
    var pct = SHIPPING_THRESHOLD > 0 ? Math.min(100, (totalPrice / SHIPPING_THRESHOLD) * 100) : 0;
    var qualified = totalPrice >= SHIPPING_THRESHOLD;

    if (fill) fill.style.width = pct + '%';
    if (qualified) bar.classList.add('is-qualified');
    else bar.classList.remove('is-qualified');

    if (messageEl) {
      if (qualified) {
        messageEl.innerHTML = SHIPPING_QUALIFIED_MSG;
      } else {
        var remainingCents = SHIPPING_THRESHOLD - totalPrice;
        var remainingFmt = formatMoney(remainingCents);
        messageEl.innerHTML = SHIPPING_PROGRESS_MSG.replace(/\{remaining\}/g, '<b>' + escapeHtml(remainingFmt) + '</b>');
      }
    }
  }

  function updateDiscounts(cart) {
    var container = qs(SELECTORS.cartDiscounts, drawer);
    if (!container) return;
    if (!cart.cart_level_discount_applications || !cart.cart_level_discount_applications.length) {
      container.innerHTML = '';
      container.style.display = 'none';
      return;
    }
    container.style.display = '';
    var html = '';
    cart.cart_level_discount_applications.forEach(function (d) {
      html += '<div class="fs-cart-footer__discount-row">' +
        '<span>' + escapeHtml(d.title) + '</span>' +
        '<span>-' + formatMoney(d.total_allocated_amount) + '</span>' +
        '</div>';
    });
    container.innerHTML = html;
  }

  function updateItems(cart) {
    var container = qs(SELECTORS.cartItems, drawer);
    if (!container) return;
    var html = '';
    // Render non-GWP first, GWP after — preserves the visual order shipped by the legacy drawer
    var gwp = [];
    cart.items.forEach(function (item) {
      if (item.properties && item.properties._gwp === 'true') {
        gwp.push(item);
      } else {
        html += buildItemHTML(item, false);
      }
    });
    gwp.forEach(function (item) { html += buildItemHTML(item, true); });
    container.innerHTML = html;
  }

  function buildItemHTML(item, isGwp) {
    var imgHtml = '';
    if (item.image) {
      var imgSrc = getSizedImageUrl(item.image, '180x');
      imgHtml = '<img src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(item.product_title || item.title || '') + '" width="84" loading="lazy">';
    }

    var variantHtml = '';
    if (item.options_with_values && item.options_with_values.length) {
      item.options_with_values.forEach(function (opt) {
        if (opt.value && opt.value !== 'Default Title') {
          variantHtml += '<div class="fs-cart-item__variant">' + escapeHtml(opt.name) + ': ' + escapeHtml(opt.value) + '</div>';
        }
      });
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      variantHtml = '<div class="fs-cart-item__variant">' + escapeHtml(item.variant_title) + '</div>';
    }

    var sellingPlanHtml = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      sellingPlanHtml = '<div class="fs-cart-item__selling-plan">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + '</div>';
    }

    var propsHtml = '';
    if (item.properties) {
      var rows = [];
      for (var key in item.properties) {
        if (!item.properties.hasOwnProperty(key)) continue;
        var val = item.properties[key];
        if (!val) continue;
        if (key.charAt(0) === '_') continue;
        rows.push('<span>' + escapeHtml(key) + ': ' + escapeHtml(val) + '</span>');
      }
      if (rows.length) propsHtml = '<div class="fs-cart-item__properties">' + rows.join('<br>') + '</div>';
    }

    var qtyHtml;
    var priceHtml;
    if (isGwp) {
      qtyHtml = '<span class="fs-cart-item__gwp-qty">x' + item.quantity + '</span>';
      priceHtml = '<span class="fs-cart-item__price--gwp">' + (window.themeStrings && window.themeStrings.complimentary ? window.themeStrings.complimentary : 'Complimentary') + '</span>';
    } else {
      qtyHtml =
        '<div class="fs-cart-qty">' +
          '<button type="button" data-action="decrease" data-key="' + escapeHtml(item.key) + '" aria-label="Decrease quantity">' +
            '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M17.543 11.029H2.1A1.032 1.032 0 0 1 1.071 10c0-.566.463-1.029 1.029-1.029h15.443c.566 0 1.029.463 1.029 1.029 0 .566-.463 1.029-1.029 1.029z"/></svg>' +
          '</button>' +
          '<input type="number" value="' + item.quantity + '" min="1" data-key="' + escapeHtml(item.key) + '" data-action="quantity-input" aria-label="Quantity">' +
          '<button type="button" data-action="increase" data-key="' + escapeHtml(item.key) + '" aria-label="Increase quantity">' +
            '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M17.409 8.929h-6.695V2.258c0-.566-.506-1.029-1.071-1.029s-1.071.463-1.071 1.029v6.671H1.967C1.401 8.929.938 9.435.938 10s.463 1.071 1.029 1.071h6.605V17.7c0 .566.506 1.029 1.071 1.029s1.071-.463 1.071-1.029v-6.629h6.695c.566 0 1.029-.506 1.029-1.071s-.463-1.071-1.029-1.071z"/></svg>' +
          '</button>' +
        '</div>';

      if (item.original_price !== item.final_price) {
        priceHtml =
          '<span class="fs-cart-item__price--original">' + formatMoney(item.original_price) + '</span>' +
          '<span class="fs-cart-item__price--sale">' + formatMoney(item.final_price) + '</span>';
      } else {
        priceHtml = '<span>' + formatMoney(item.original_price) + '</span>';
      }
      if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
        item.line_level_discount_allocations.forEach(function (d) {
          priceHtml += '<div class="fs-cart-item__line-discount">' +
            escapeHtml(d.discount_application.title) + ' (-' + formatMoney(d.amount) + ')' +
            '</div>';
        });
      }
    }

    var removeHtml = isGwp ? '' :
      '<button type="button" class="fs-cart-item__remove" data-action="remove" data-key="' + escapeHtml(item.key) + '" aria-label="Remove item">' +
        '<svg viewBox="0 0 64 64" aria-hidden="true"><path d="M19 17.61l27.12 27.13m0-27.12L19 44.74" stroke="currentColor" fill="none"/></svg>' +
      '</button>';

    var gwpLabelHtml = isGwp ? '<div class="fs-cart-item__gwp-label">' + (window.themeStrings && window.themeStrings.complimentary ? window.themeStrings.complimentary : 'Complimentary') + '</div>' : '';

    return '<div class="fs-cart-item' + (isGwp ? ' fs-cart-item--gwp' : '') + '" data-key="' + escapeHtml(item.key) + '" data-item-id="' + item.id + '">' +
      '<div class="fs-cart-item__image"><a href="' + escapeHtml(item.url) + '" tabindex="-1">' + imgHtml + '</a></div>' +
      '<div class="fs-cart-item__details">' +
        '<div class="fs-cart-item__name"><a href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title || item.title) + '</a></div>' +
        variantHtml + sellingPlanHtml + propsHtml + gwpLabelHtml +
        '<div class="fs-cart-item__bottom">' +
          qtyHtml +
          '<div class="fs-cart-item__price">' + priceHtml + '</div>' +
        '</div>' +
      '</div>' +
      removeHtml +
    '</div>';
  }

  function updateEmptyState(count) {
    if (count === 0) drawer.classList.add('is-empty');
    else drawer.classList.remove('is-empty');
  }

  function updateHeaderBubble(count) {
    // Update the header cart icon counter text. The de theme renders both
    // " (N)" inline (sections/header.liquid) and a .cart-count-bubble dot
    // (sections/gaia-header.liquid). Sync both.
    qsa(SELECTORS.cartBubble).forEach(function (el) {
      // Inline span: <span aria-hidden="true"> (N)</span>
      var inlineSpan = el.querySelector('span[aria-hidden="true"]:not(.cart-count-bubble)');
      if (inlineSpan) {
        if (count > 0 && count < 100) inlineSpan.textContent = ' (' + count + ')';
        else inlineSpan.textContent = '';
      }
      // Bubble dot variant (gaia header)
      var bubble = el.querySelector('.cart-count-bubble');
      if (bubble) {
        bubble.style.display = count > 0 ? '' : 'none';
      }
    });
  }

  // ─── Event delegation ──────────────────────────────
  function handleDrawerClick(e) {
    var actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;
    var action = actionEl.getAttribute('data-action');
    var key = actionEl.getAttribute('data-key');

    switch (action) {
      case 'close-drawer':
        closeDrawer();
        break;
      case 'remove':
        e.preventDefault();
        removeItem(key, actionEl);
        break;
      case 'decrease':
        e.preventDefault();
        var inputMinus = actionEl.closest('.fs-cart-qty').querySelector('input');
        var cur = parseInt(inputMinus.value, 10);
        if (cur <= 1) {
          removeItem(key, actionEl);
        } else {
          // Optimistically update input so rapid clicks accumulate rather than
          // all reading the same stale value while the queue drains.
          inputMinus.value = cur - 1;
          updateQuantity(key, cur - 1);
        }
        break;
      case 'increase':
        e.preventDefault();
        var inputPlus = actionEl.closest('.fs-cart-qty').querySelector('input');
        var nextQty = parseInt(inputPlus.value, 10) + 1;
        inputPlus.value = nextQty;
        updateQuantity(key, nextQty);
        break;
      case 'add-recommendation':
        e.preventDefault();
        var variantId = parseInt(actionEl.getAttribute('data-variant-id'), 10);
        if (!variantId) break;
        actionEl.classList.add('fs-is-loading');
        actionEl.disabled = true;
        addToCart(variantId, 1).finally(function () {
          actionEl.classList.remove('fs-is-loading');
          actionEl.disabled = false;
        });
        break;
    }
  }

  function handleDrawerChange(e) {
    if (e.target.getAttribute && e.target.getAttribute('data-action') === 'quantity-input') {
      var key = e.target.getAttribute('data-key');
      var val = parseInt(e.target.value, 10);
      if (isNaN(val) || val < 0) return;
      if (val === 0) removeItem(key, e.target);
      else updateQuantity(key, val);
    }
  }

  // ─── Add-to-cart form interception ─────────────────
  function handleAddToCartForm(form) {
    if (form.getAttribute('data-fs-adding')) return;
    form.setAttribute('data-fs-adding', 'true');

    var formData = new FormData(form);
    var item = {};
    var idInput = form.querySelector('[name="id"]');
    if (idInput) item.id = parseInt(idInput.value, 10);
    var qtyInput = form.querySelector('[name="quantity"]');
    item.quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    var props = {};
    formData.forEach(function (val, key) {
      if (key.indexOf('properties[') === 0) {
        var propKey = key.replace('properties[', '').replace(']', '');
        props[propKey] = val;
      }
    });
    if (Object.keys(props).length) item.properties = props;
    var sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = sellingPlan;

    if (!item.id) {
      form.removeAttribute('data-fs-adding');
      return;
    }

    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.classList.add('fs-is-loading');

    function done() {
      if (submitBtn) submitBtn.classList.remove('fs-is-loading');
      form.removeAttribute('data-fs-adding');
    }

    enqueueCartOp(function () {
      drawer && drawer.classList.add('is-loading');
      return fetchJSON(ROUTES.cartAdd, {
        method: 'POST',
        body: JSON.stringify({ items: [item] })
      }).then(function () {
        return refreshDrawer();
      }).then(function () {
        openDrawer();
        if (drawer) drawer.classList.remove('is-loading');
        done();
        notifyCartUpdate(null, 'fs-cart-drawer:add-form');
      }).catch(function (err) {
        console.error('FsCart add error:', err);
        if (drawer) drawer.classList.remove('is-loading');
        done();
        return refreshDrawer().then(function () {
          openDrawer();
          showCartError(err);
        });
      });
    });
  }

  function interceptAddToCart() {
    // Capture phase — fires before product-form.js's bubble-phase submit handler,
    // letting us short-circuit Dawn's notification/redirect flow.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('form[action*="/cart/add"] [type="submit"]');
      if (!btn) return;
      var form = btn.closest('form[action*="/cart/add"]');
      if (!form) return;
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      handleAddToCartForm(form);
    }, true);

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      handleAddToCartForm(form);
    }, true);
  }

  // ─── Cart icon click ───────────────────────────────
  function bindCartIconLinks() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest(SELECTORS.cartIconLinks);
      if (!link) return;
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      if (isOpen) closeDrawer();
      else refreshDrawer().then(openDrawer);
    });
  }

  // ─── Keyboard ──────────────────────────────────────
  function handleKeydown(e) {
    if (e.key === 'Escape' && isOpen) closeDrawer();
  }

  // ─── Init ──────────────────────────────────────────
  function init() {
    drawer = qs(SELECTORS.drawer);
    if (!drawer) return;

    SHIPPING_ENABLED = drawer.getAttribute('data-shipping-enable') === 'true';
    SHIPPING_THRESHOLD = parseInt(drawer.getAttribute('data-shipping-threshold'), 10) || 0;
    SHIPPING_PROGRESS_MSG = drawer.getAttribute('data-shipping-progress') || '';
    SHIPPING_QUALIFIED_MSG = drawer.getAttribute('data-shipping-qualified') || '';
    CURRENCY = drawer.getAttribute('data-currency') || CURRENCY;

    drawer.addEventListener('click', handleDrawerClick);
    drawer.addEventListener('change', handleDrawerChange);

    bindCartIconLinks();
    interceptAddToCart();

    document.addEventListener('keydown', handleKeydown);

    // Prime the shipping bar from the server-rendered cart state on first paint
    fetchCart().then(function (cart) {
      if (cart.currency) CURRENCY = cart.currency;
      updateShippingBar(cart.total_price);
      updateHeaderBubble(cart.item_count);
      updateCartCount(cart.item_count);
    }).catch(function () {});
  }

  // ─── Public API ────────────────────────────────────
  window.FsCart = {
    open: function () {
      if (isOpen) { closeDrawer(); return; }
      refreshDrawer().then(openDrawer);
    },
    close: function () { closeDrawer(); },
    refresh: function () { return refreshDrawer(); },
    add: function (variantId, qty, props) { return addToCart(variantId, qty, props); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
